import React, { createContext, useState, useCallback, useRef } from 'react';
import { getApiErrorMessage } from '@shared/utils/apiError';
import type { ReactNode } from 'react';
import type {
  WizardState,
  BasicInfo,
  Contact,
  Onboarding,
  OperatingHour,
  ValidationErrors,
  StepValidationErrors,
  DuplicateCheckResult,
} from '../../types/wizard';
import {
  WIZARD_STEPS,
  TOTAL_STEPS,
  getDefaultOperatingHours,
  validateBasicInfo,
  validateContact,
  validateOnboarding,
  validateOperatingHours,
} from '../../types/wizard';
import apiClient from '@shared/services/apiClient';
import { clinicApi } from '../../services/clinicApi';
import { useWizardNavigation } from './core/useWizardNavigation';

export interface WizardContextValue {
  // State
  state: WizardState;
  stepErrors: StepValidationErrors;
  isSubmitting: boolean;
  submitError: string | null;
  isCheckingDuplicates: boolean;

  // Actions
  nextStep: () => Promise<boolean>;
  prevStep: () => void;
  goToStep: (step: number) => void;
  updateBasicInfo: (data: Partial<BasicInfo>) => void;
  updateOperatingHour: (dayOfWeek: number, updates: Partial<OperatingHour>) => void;
  copyMondayToWeekdays: () => void;
  updateContact: (data: Partial<Contact>) => void;
  updateOnboarding: (data: Partial<Onboarding>) => void;
  updateCategoryTypeIds: (ids: number[]) => void;
  updateCapabilityTypeIds: (ids: number[]) => void;
  updateServiceProcedureIds: (ids: number[]) => void;
  setIncludeCapabilities: (include: boolean) => void;
  setIncludeOnboarding: (include: boolean) => void;
  setIncludeContact: (include: boolean) => void;
  setAcknowledgedDuplicateWarning: (acknowledged: boolean) => void;
  reset: () => void;
  // Returns the created clinic id (null when creation itself failed) plus the
  // labels of any post-create sections that failed to save. The clinic exists
  // whenever clinicId is non-null — callers must surface warnings, not retry
  // the whole creation.
  submit: () => Promise<{ clinicId: number | null; warnings: string[] }>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const WizardContext = createContext<WizardContextValue | undefined>(undefined);

const getInitialState = (): WizardState => ({
  currentStep: 0,
  basicInfo: {
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    phone: '',
    fax: '',
    email: '',
    website: '',
  },
  operatingHours: getDefaultOperatingHours(),
  contact: {
    name: '',
    phone: '',
    email: '',
    role: 'Primary',
  },
  onboarding: {
    preferredMethodOfAppointment: '',
    electronicChainOfCustody: '',
    reportingMethod: '',
    dotCertification: false,
    numberOfCertifiedDOTProfessionals: undefined,
    w9OnFile: false,
    eScreenCode: '',
    formFoxCode: '',
    questCode: '',
  },
  categoryTypeIds: [],
  capabilityTypeIds: [],
  serviceProcedureIds: [],
  includeCapabilities: true,
  includeOnboarding: true,
  includeContact: true,
  acknowledgedDuplicateWarning: false,
  duplicateCheckResult: null,
});

interface WizardProviderProps {
  children: ReactNode;
}

export const WizardProvider: React.FC<Readonly<WizardProviderProps>> = ({ children }) => {
  const [state, setState] = useState<WizardState>(getInitialState());
  const [stepErrors, setStepErrors] = useState<StepValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  // Ref to track the latest acknowledgedDuplicateWarning value for use in
  // async callbacks (useCallback closures capture stale state).
  const acknowledgedRef = useRef(state.acknowledgedDuplicateWarning);

  const validateCurrentStep = useCallback((): ValidationErrors => {
    switch (state.currentStep) {
      case WIZARD_STEPS.BASIC_INFO:
        return validateBasicInfo(state.basicInfo);
      case WIZARD_STEPS.HOURS:
        return validateOperatingHours(state.operatingHours);
      case WIZARD_STEPS.CAPABILITIES:
        return {}; // list selections only — nothing to validate
      case WIZARD_STEPS.ONBOARDING:
        return validateOnboarding(state.onboarding, state.includeOnboarding);
      case WIZARD_STEPS.CONTACT:
        return validateContact(state.contact, state.includeContact);
      case WIZARD_STEPS.REVIEW:
        return {};
      default:
        return {};
    }
  }, [state]);

  const checkForDuplicates = useCallback(async (): Promise<DuplicateCheckResult | null> => {
    try {
      setIsCheckingDuplicates(true);
      const result = await clinicApi.checkDuplicate(
        state.basicInfo.name,
        state.basicInfo.city || '',
        state.basicInfo.state || '',
        state.basicInfo.zip || ''
      );
      setState(prev => ({
        ...prev,
        duplicateCheckResult: result,
      }));
      return result;
    } catch (error) {
      console.error('Failed to check for duplicates:', error);
      return null;
    } finally {
      setIsCheckingDuplicates(false);
    }
  }, [state.basicInfo.name, state.basicInfo.city, state.basicInfo.state, state.basicInfo.zip]);

  // Shared navigation via useWizardNavigation hook
  const isStepSkipped = useCallback((stepIndex: number): boolean => {
    if (stepIndex === WIZARD_STEPS.CAPABILITIES && !state.includeCapabilities) return true;
    if (stepIndex === WIZARD_STEPS.ONBOARDING && !state.includeOnboarding) return true;
    if (stepIndex === WIZARD_STEPS.CONTACT && !state.includeContact) return true;
    return false;
  }, [state.includeCapabilities, state.includeOnboarding, state.includeContact]);

  const beforeNext = useCallback(async (): Promise<boolean> => {
    const errors = validateCurrentStep();
    setStepErrors((prev) => ({ ...prev, [state.currentStep]: errors }));
    if (Object.keys(errors).length > 0) return false;

    // After basic info step, check for duplicates.
    // Read from acknowledgedRef (always current) instead of the stale closure
    // value, so that clicking "Continue" in the duplicate modal works correctly.
    if (state.currentStep === WIZARD_STEPS.BASIC_INFO && !acknowledgedRef.current) {
      const duplicateResult = await checkForDuplicates();
      if (duplicateResult?.hasPotentialDuplicates) return false;
    }

    return true;
  }, [state.currentStep, validateCurrentStep, checkForDuplicates]);

  const setCurrentStep = useCallback((step: number) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const { goToStep, nextStep, prevStep } = useWizardNavigation({
    totalSteps: TOTAL_STEPS,
    currentStep: state.currentStep,
    setCurrentStep,
    isStepSkipped,
    beforeNext,
  });

  const updateBasicInfo = useCallback((data: Partial<BasicInfo>) => {
    acknowledgedRef.current = false;
    setState((prev) => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo,
        ...data,
      },
      // Reset duplicate check when basic info changes (name or address)
      acknowledgedDuplicateWarning: false,
      duplicateCheckResult: null,
    }));
  }, []);

  const updateOperatingHour = useCallback((dayOfWeek: number, updates: Partial<OperatingHour>) => {
    setState((prev) => ({
      ...prev,
      operatingHours: prev.operatingHours.map((hour) =>
        hour.dayOfWeek === dayOfWeek ? { ...hour, ...updates } : hour
      ),
    }));
  }, []);

  const copyMondayToWeekdays = useCallback(() => {
    setState((prev) => {
      const monday = prev.operatingHours.find((h) => h.dayOfWeek === 1);
      if (!monday) return prev;
      return {
        ...prev,
        operatingHours: prev.operatingHours.map((hour) =>
          hour.dayOfWeek >= 2 && hour.dayOfWeek <= 5
            ? { ...hour, isClosed: monday.isClosed, openTime: monday.openTime, closeTime: monday.closeTime }
            : hour
        ),
      };
    });
  }, []);

  const updateContact = useCallback((data: Partial<Contact>) => {
    setState((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        ...data,
      },
    }));
  }, []);

  const updateOnboarding = useCallback((data: Partial<Onboarding>) => {
    setState((prev) => ({
      ...prev,
      onboarding: {
        ...prev.onboarding,
        ...data,
      },
    }));
  }, []);

  const updateCategoryTypeIds = useCallback((ids: number[]) => {
    setState((prev) => ({ ...prev, categoryTypeIds: ids }));
  }, []);

  const updateCapabilityTypeIds = useCallback((ids: number[]) => {
    setState((prev) => ({ ...prev, capabilityTypeIds: ids }));
  }, []);

  const updateServiceProcedureIds = useCallback((ids: number[]) => {
    setState((prev) => ({ ...prev, serviceProcedureIds: ids }));
  }, []);

  const setIncludeCapabilities = useCallback((include: boolean) => {
    setState((prev) => ({
      ...prev,
      includeCapabilities: include,
    }));
  }, []);

  const setIncludeOnboarding = useCallback((include: boolean) => {
    setState((prev) => ({
      ...prev,
      includeOnboarding: include,
    }));
  }, []);

  const setIncludeContact = useCallback((include: boolean) => {
    setState((prev) => ({
      ...prev,
      includeContact: include,
    }));
  }, []);

  const setAcknowledgedDuplicateWarning = useCallback((acknowledged: boolean) => {
    acknowledgedRef.current = acknowledged;
    setState((prev) => ({
      ...prev,
      acknowledgedDuplicateWarning: acknowledged,
    }));
  }, []);

  const reset = useCallback(() => {
    setState(getInitialState());
    setStepErrors({});
    setIsSubmitting(false);
    setSubmitError(null);
  }, []);

  const submit = useCallback(async (): Promise<{ clinicId: number | null; warnings: string[] }> => {
    // Validate all required steps
    const basicInfoErrors = validateBasicInfo(state.basicInfo);
    const hoursErrors = validateOperatingHours(state.operatingHours);
    const onboardingErrors = validateOnboarding(state.onboarding, state.includeOnboarding);
    const contactErrors = validateContact(state.contact, state.includeContact);

    if (
      Object.keys(basicInfoErrors).length > 0 ||
      Object.keys(hoursErrors).length > 0 ||
      Object.keys(onboardingErrors).length > 0 ||
      Object.keys(contactErrors).length > 0
    ) {
      setStepErrors({
        [WIZARD_STEPS.BASIC_INFO]: basicInfoErrors,
        [WIZARD_STEPS.HOURS]: hoursErrors,
        [WIZARD_STEPS.ONBOARDING]: onboardingErrors,
        [WIZARD_STEPS.CONTACT]: contactErrors,
      });
      setSubmitError('Please fix validation errors before submitting');
      return { clinicId: null, warnings: [] };
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Build the request body matching CreateClinicRequest DTO.
      // No `code` is sent — usp_CreateClinic assigns the next sequential
      // clinic code server-side when none is supplied (SLV2-2819).
      const requestBody: Record<string, unknown> = {
        name: state.basicInfo.name.trim(),
        phone: state.basicInfo.phone?.trim() || undefined,
        fax: state.basicInfo.fax?.trim() || undefined,
        email: state.basicInfo.email?.trim() || undefined,
        website: state.basicInfo.website?.trim()
          ? (state.basicInfo.website.trim().match(/^https?:\/\//)
              ? state.basicInfo.website.trim()
              : `https://${state.basicInfo.website.trim()}`)
          : undefined,
        acknowledgedDuplicateWarning: state.acknowledgedDuplicateWarning,

        // Address fields (required — merged into basic info)
        includeAddress: true,
        address1: (state.basicInfo.address1 || '').trim(),
        address2: state.basicInfo.address2?.trim() || undefined,
        city: (state.basicInfo.city || '').trim(),
        state: (state.basicInfo.state || '').trim(),
        zip: (state.basicInfo.zip || '').trim(),
        country: state.basicInfo.country || 'US',
      };

      // Contact fields (optional)
      if (state.includeContact && state.contact.name.trim()) {
        requestBody.includeContact = true;
        requestBody.contactName = state.contact.name.trim();
        requestBody.contactPhone = state.contact.phone?.trim() || undefined;
        requestBody.contactEmail = state.contact.email?.trim() || undefined;
        requestBody.contactRole = state.contact.role;
      }

      // NOTE: onboarding fields are deliberately NOT part of the create body.
      // CreateClinicRequest has no onboarding properties, so the server used to
      // silently drop them (SLV2-3153). They are persisted via the dedicated
      // PUT /clinics/{id}/onboarding call chained after creation below.

      // Call the API to create the clinic
      const response = await apiClient.post('/clinics', requestBody);

      if (response.data && response.data.id) {
        const clinicId = response.data.id;

        // Post-create saves. Each call is independent and non-fatal — the
        // clinic already exists — but failures must be SURFACED, not swallowed
        // (SLV2-3153): collect the failed section labels for the caller.
        const warnings: string[] = [];
        const attempt = async (label: string, save: () => Promise<unknown>) => {
          try {
            await save();
          } catch (error) {
            console.error(`Failed to save ${label} (clinic was created):`, error);
            warnings.push(`${label} (${getApiErrorMessage(error)})`);
          }
        };

        // NOTE: there is no capability-flags call any more. The Emergency Room /
        // After Hours / PAT / X-Ray tiles were removed (Clinic Network decision
        // 2026-09-16); the server derives those clinic columns from the
        // category / capability / services saves below.

        // Operating hours are always saved — the step defaults to Mon-Fri 8-5.
        await attempt('operating hours', () => {
          const getTime = (dayOfWeek: number, type: 'open' | 'close'): string | null => {
            const hour = state.operatingHours.find((h) => h.dayOfWeek === dayOfWeek);
            if (!hour || hour.isClosed) return null;
            return type === 'open' ? hour.openTime : hour.closeTime;
          };
          return clinicApi.updateOperatingHours(clinicId, {
            sunStart: getTime(0, 'open'), sunEnd: getTime(0, 'close'),
            monStart: getTime(1, 'open'), monEnd: getTime(1, 'close'),
            tueStart: getTime(2, 'open'), tueEnd: getTime(2, 'close'),
            wedStart: getTime(3, 'open'), wedEnd: getTime(3, 'close'),
            thurStart: getTime(4, 'open'), thurEnd: getTime(4, 'close'),
            friStart: getTime(5, 'open'), friEnd: getTime(5, 'close'),
            satStart: getTime(6, 'open'), satEnd: getTime(6, 'close'),
          });
        });

        // Onboarding columns are only writable via PUT /clinics/{id}/onboarding
        // (usp_UpdateClinicOnboarding overwrites every column it covers). The
        // three vendor codes come from the onboarding step's vendor rows.
        if (state.includeOnboarding) {
          await attempt('onboarding details', () =>
            clinicApi.updateClinicOnboarding(clinicId, {
              preferredMethodOfAppointment: state.onboarding.preferredMethodOfAppointment || undefined,
              electronicChainOfCustody: state.onboarding.electronicChainOfCustody || undefined,
              reportingMethod: state.onboarding.reportingMethod || undefined,
              dotCertification: state.onboarding.dotCertification || false,
              numberOfCertifiedDOTProfessionals: state.onboarding.numberOfCertifiedDOTProfessionals || undefined,
              w9OnFile: state.onboarding.w9OnFile || false,
              eScreenCode: state.onboarding.eScreenCode?.trim() || undefined,
              formFoxCode: state.onboarding.formFoxCode?.trim() || undefined,
              questCode: state.onboarding.questCode?.trim() || undefined,
            })
          );
        }

        // Dynamic lookup selections (categories / capability list / services) —
        // split into separate attempts so one failure can't skip the rest.
        if (state.categoryTypeIds.length > 0) {
          await attempt('categories', () => clinicApi.updateCategories(clinicId, state.categoryTypeIds));
        }
        if (state.capabilityTypeIds.length > 0) {
          await attempt('capability list', () => clinicApi.updateCapabilityList(clinicId, state.capabilityTypeIds));
        }
        if (state.serviceProcedureIds.length > 0) {
          await attempt('services', () =>
            clinicApi.updateClinicServices(clinicId, {
              // SLV2-3452: fee entry is admin-only; new procedures start at 0 on the server
              services: state.serviceProcedureIds.map((procedureId) => ({ procedureId, fee: null })),
            })
          );
        }

        return { clinicId, warnings };
      }

      setSubmitError('Failed to create clinic: No ID returned');
      return { clinicId: null, warnings: [] };
    } catch (error: unknown) {
      console.error('Failed to create clinic:', error);
      setSubmitError(getApiErrorMessage(error, 'Failed to create clinic. Please try again.'));
      return { clinicId: null, warnings: [] };
    } finally {
      setIsSubmitting(false);
    }
  }, [state]);

  const value: WizardContextValue = {
    state,
    stepErrors,
    isSubmitting,
    submitError,
    isCheckingDuplicates,
    nextStep,
    prevStep,
    goToStep,
    updateBasicInfo,
    updateOperatingHour,
    copyMondayToWeekdays,
    updateContact,
    updateOnboarding,
    updateCategoryTypeIds,
    updateCapabilityTypeIds,
    updateServiceProcedureIds,
    setIncludeCapabilities,
    setIncludeOnboarding,
    setIncludeContact,
    setAcknowledgedDuplicateWarning,
    reset,
    submit,
  };

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

